import { emailRegex } from '@/constants';
import type { IAuthFormFields, IBaseForm } from '@/types';
import { hasEmptyFields } from '@/utils';
import { Box, Button, Flex, Link, useDisclosure } from '@chakra-ui/react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ERRORS, hasSpecialChar, hasUpperLetter, minLength } from '../../constants';
import { MbeInput, PasswordInput } from '../shared';

export const LoginForm: React.FC<IBaseForm<IAuthFormFields>> = ({
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const {
    formState: { errors },
    handleSubmit: submitHandler,
    register,
    watch,
  } = useForm<IAuthFormFields>({ mode: 'all', criteriaMode: 'all' });

  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const areRequiredFieldsEmpty = hasEmptyFields(watch());
  const isDisabled = !!errors.email?.types || !!errors.password?.types || areRequiredFieldsEmpty;

  const handleSubmit: SubmitHandler<IAuthFormFields> = (data, event): void => {
    event?.preventDefault();
    providedOnSubmit?.(data);
  };

  return (
    <form
      autoComplete="off"
      id="loginForm"
      name="loginForm"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={submitHandler(handleSubmit)}
    >
      <Flex direction="column" gap={12} mb={8}>
        <MbeInput
          error={errors.email}
          id="email"
          placeholder={t('FORM.EMAIL')}
          {...register('email', {
            required: { value: true, message: t(ERRORS.empty) },
            pattern: { value: emailRegex, message: t(ERRORS.email) },
          })}
        />

        <PasswordInput
          error={errors.password}
          id="password"
          placeholder={t('FORM.PASSWORD')}
          {...register('password', {
            required: true,
            validate: {
              minLength: (value) => minLength(value, 8),
              hasUpperLetter,
              hasSpecialChar,
            },
            onBlur: onClose,
            onChange: () => {
              if (!isOpen) onOpen();
            },
          })}
        />
      </Flex>
      <Box mb={8}>
        <Link color="brand.red.light" fontWeight="medium" href="#">
          {t('FORM.FORGOT_PASSWORD')}
        </Link>
      </Box>
      <Flex flexDirection="row-reverse" gap={2} justifyContent="space-between">
        <Button disabled={isDisabled} type="submit" variant="mbePrimary">
          {t('ACTIONS.NEXT')}
        </Button>
      </Flex>
    </form>
  );
};
